import { useEffect, useRef, useState, type CSSProperties } from "react";
import { statusColorOf, type StatusColor, type StatusTint } from "@/lib/tokens";

export type PillStyle = "tint" | "outline" | "dot";

export interface StatusOptionLike {
  color: StatusColor | string;
  label: string;
}

interface StatusPillProps {
  option: StatusOptionLike | null | undefined;
  pillStyle?: PillStyle;
  size?: "xs" | "sm" | "md";
  onClick?: () => void;
  /** Cap the pill width and ellipsize a long label (e.g. inside the status
   * picker popover, where a long option would otherwise overflow the box). */
  maxWidth?: number | string;
}

export function StatusPill({
  option,
  pillStyle = "tint",
  size = "sm",
  onClick,
  maxWidth,
}: StatusPillProps) {
  const tint = statusColorOf(option?.color);

  // Change motion (crossfade + settle-pop). Fires ONLY on a real option change,
  // never on first paint — so the picker popover's static pills and a freshly-
  // rendered table stay still. Hooks run unconditionally (before the null
  // guard) to respect the rules of hooks.
  const sig = `${option?.color ?? ""}|${option?.label ?? ""}`;
  const prevSigRef = useRef(sig);
  const prevTintRef = useRef<StatusTint>(tint);
  const [fadeTint, setFadeTint] = useState<StatusTint | null>(null);
  const [pop, setPop] = useState(false);

  useEffect(() => {
    if (prevSigRef.current !== sig) {
      // Only the tint variant paints a bg worth crossfading; outline/dot just
      // pop. A cleared pill (no option now) also skips the overlay.
      if (pillStyle === "tint" && option) setFadeTint(prevTintRef.current);
      setPop(true);
      prevSigRef.current = sig;
    }
    prevTintRef.current = tint;
  }, [sig, tint, pillStyle, option]);

  if (!option) return null;

  const pad = size === "xs" ? "2px 7px" : size === "sm" ? "3px 9px" : "5px 12px";
  const font = size === "xs" ? 11 : size === "sm" ? 12 : 13;
  const base: CSSProperties = {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: pad,
    borderRadius: 999,
    fontSize: font,
    fontWeight: 500,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    cursor: onClick ? "pointer" : "default",
    transition: "transform .12s, box-shadow .12s",
    fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
    ...(maxWidth !== undefined ? { maxWidth, minWidth: 0 } : {}),
  };
  let variant: CSSProperties = {};
  if (pillStyle === "tint")
    // bg/fg are applied by the .flow-status-tint CSS class from these hue vars,
    // so dark mode can restyle them (light mode reads --pill-bg/--pill-fg as-is;
    // dark derives a translucent chip + bright text from --pill-dot).
    variant = {
      "--pill-bg": tint.bg,
      "--pill-fg": tint.fg,
      "--pill-dot": tint.dot,
    } as CSSProperties;
  else if (pillStyle === "outline")
    variant = {
      background: "transparent",
      color: tint.fg,
      boxShadow: `inset 0 0 0 1px ${tint.dot}`,
    };
  else
    variant = {
      background: "transparent",
      color: "var(--ink-1)",
      padding: size === "xs" ? "2px 4px" : "3px 6px",
    };

  const tintClass = pillStyle === "tint" ? "flow-status-tint" : "";
  const className = `${tintClass}${pop ? " flow-status-pill-pop" : ""}`.trim() || undefined;

  return (
    <span
      onClick={onClick}
      className={className}
      onAnimationEnd={(e) => {
        // Only the pop animation lives on this element (the crossfade lives on
        // the overlay child); clear the one-shot pop when it ends.
        if (e.target === e.currentTarget) setPop(false);
      }}
      title={maxWidth !== undefined ? option.label : undefined}
      style={{ ...base, ...variant }}
      onMouseEnter={(e) => {
        if (!onClick) return;
        const shadow = (variant.boxShadow as string | undefined) ?? "";
        e.currentTarget.style.boxShadow = shadow
          ? `${shadow}, 0 0 0 3px ${tint.bg}80`
          : `0 0 0 3px ${tint.bg}80`;
      }}
      onMouseLeave={(e) => {
        if (!onClick) return;
        e.currentTarget.style.boxShadow = (variant.boxShadow as string | undefined) ?? "";
      }}
    >
      {fadeTint ? (
        <span
          aria-hidden="true"
          className="flow-status-tint flow-status-pill-fade"
          onAnimationEnd={() => setFadeTint(null)}
          style={
            {
              position: "absolute",
              inset: 0,
              borderRadius: 999,
              zIndex: 0,
              pointerEvents: "none",
              "--pill-bg": fadeTint.bg,
              "--pill-fg": fadeTint.fg,
              "--pill-dot": fadeTint.dot,
            } as CSSProperties
          }
        ></span>
      ) : null}
      <span
        style={{
          position: "relative",
          zIndex: 1,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          minWidth: 0,
        }}
      >
        {pillStyle === "dot" ? (
          <span
            style={{ width: 8, height: 8, borderRadius: 999, background: tint.dot, flexShrink: 0 }}
          ></span>
        ) : null}
        {maxWidth !== undefined ? (
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {option.label}
          </span>
        ) : (
          option.label
        )}
      </span>
    </span>
  );
}
